import { useLaunchDarkly } from '@bestseller/retail-planning.launchdarkly';
import { useAuthentication } from '@bestseller-bit/frontend-community.utilities.authentication';
import { Box, CircularProgress, Link, Stack, styled, Typography } from '@mui/material';
import { useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Container from './components/atoms/Container';
import { StickyHelpButton } from './components/molecules/StickyHelpButton/StickyHelpButton';
import { Drawer } from './components/organisms/Drawer/Drawer';
import { useStores } from './hooks/store-selection/queries/useStores';
import { useIsStatusPage } from './hooks/useIsStatusPage';
import useScrollRestoration from './hooks/useScrollRestoration';
import { Router } from './Router';

export const AppContent = () => {
    const { account } = useAuthentication();

    useLaunchDarkly(account?.username ?? '');

    const isStatusPage = useIsStatusPage();

    const scrollableRef = useRef<HTMLElement>(null);
    useScrollRestoration(scrollableRef);

    const { data: storesData, loading: storesLoading } = useStores();

    if (storesLoading || !storesData) {
        return (
            <AppContainer>
                <StyledContainer>
                    <StyledInnerContainer sx={{ height: '100%' }}>
                        <Stack
                            sx={{
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress sx={{ color: 'black' }} />
                        </Stack>
                    </StyledInnerContainer>
                </StyledContainer>
            </AppContainer>
        );
    }

    return (
        <AppContainer>
            <ErrorBoundary
                fallback={
                    <StyledContainer>
                        <StyledInnerContainer sx={{ p: 4 }}>
                            <Container>
                                <Stack
                                    sx={{
                                        gap: '1rem',
                                    }}
                                >
                                    <Typography noWrap={false}>
                                        Sorry, something went wrong. We have logged the issue.
                                    </Typography>
                                    <Typography>
                                        If you need help, you can reach out to us via the request help link in the lower
                                        right corner or via email to{' '}
                                        <Link href="mailto:techretailplanning@bestseller.com">
                                            techretailplanning@bestseller.com
                                        </Link>
                                    </Typography>
                                    <Typography>
                                        You can try to{' '}
                                        <Link href="#" onClick={() => globalThis.location.reload()}>
                                            reload the page
                                        </Link>{' '}
                                        it might be a temporary problem. If the problem persists, please contact us.
                                    </Typography>
                                </Stack>
                            </Container>
                        </StyledInnerContainer>
                    </StyledContainer>
                }
            >
                <Drawer data-testid="drawer" />

                <StyledInnerContainer>
                    <Router />
                </StyledInnerContainer>
            </ErrorBoundary>
            {!isStatusPage && <StickyHelpButton />}
        </AppContainer>
    );
};

const AppContainer = styled(Box)(() => ({
    display: 'flex',
    height: '100vh',
    width: '100%',
    boxSizing: 'border-box',
}));

const StyledContainer = styled(Box)({
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    boxSizing: 'border-box',
});

const StyledInnerContainer = styled(Box)({
    width: '100%',
    boxSizing: 'border-box',
    height: '100%',
    overflowY: 'auto',
});
